import React, { useState } from "react";
import useOrientationLock from "../../hooks/useOrientationLock";
import RotateDeviceOverlay from "../../components/RotateDeviceOverlay";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { readableColor } from "polished";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import SchoolIcon from "@mui/icons-material/School";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import "./index.css";

// --- Datos ficticios ---

const MOCK_LEADERBOARD = [
  { rank: 1, name: "María González", avatar: null, points: 4850, streak: 12, courses: 8 },
  { rank: 2, name: "Carlos Muñoz", avatar: null, points: 4620, streak: 10, courses: 7 },
  { rank: 3, name: "Ana Rodríguez", avatar: null, points: 4310, streak: 9, courses: 7 },
  { rank: 4, name: "Pedro Soto", avatar: null, points: 3980, streak: 8, courses: 6 },
  { rank: 5, name: "Javiera López", avatar: null, points: 3750, streak: 7, courses: 6 },
  { rank: 6, name: "Diego Fernández", avatar: null, points: 3520, streak: 6, courses: 5 },
  { rank: 7, name: "Camila Torres", avatar: null, points: 3310, streak: 5, courses: 5 },
  { rank: 8, name: "Roberto Díaz", avatar: null, points: 3100, streak: 4, courses: 4 },
  { rank: 9, name: "Valentina Araya", avatar: null, points: 2890, streak: 3, courses: 4 },
  { rank: 10, name: "Andrés Vargas", avatar: null, points: 2650, streak: 2, courses: 3 },
];

const MOCK_REWARDS = [
  {
    id: 1,
    name: "Curso Premium Gratuito",
    description: "Accede a un curso especializado de tu elección sin costo adicional.",
    pointsRequired: 2000,
    icon: "school",
    category: "Formación",
  },
  {
    id: 2,
    name: "Bono de Rendimiento",
    description: "Bono de $50.000 CLP agregado a tu próxima liquidación de sueldo.",
    pointsRequired: 5000,
    icon: "gift",
    category: "Económico",
  },
  {
    id: 3,
    name: "Día Libre Adicional",
    description: "Un día de descanso extra para usar cuando lo necesites.",
    pointsRequired: 8000,
    icon: "trophy",
    category: "Bienestar",
  },
  {
    id: 4,
    name: "Gift Card Falabella",
    description: "Tarjeta de regalo por $30.000 CLP para usar en tiendas Falabella.",
    pointsRequired: 3500,
    icon: "gift",
    category: "Económico",
  },
  {
    id: 5,
    name: "Certificación Profesional",
    description: "Financiamiento completo para una certificación profesional externa.",
    pointsRequired: 10000,
    icon: "medal",
    category: "Formación",
  },
  {
    id: 6,
    name: "Kit Abastible Premium",
    description: "Mochila, botella térmica y polera oficial Abastible edición limitada.",
    pointsRequired: 1500,
    icon: "gift",
    category: "Material",
  },
  {
    id: 7,
    name: "Almuerzo con Gerencia",
    description: "Almuerzo exclusivo con el equipo de gerencia para compartir ideas.",
    pointsRequired: 6000,
    icon: "trophy",
    category: "Experiencia",
  },
  {
    id: 8,
    name: "Bono Transporte",
    description: "Bono de $20.000 CLP para transporte durante un mes.",
    pointsRequired: 2500,
    icon: "gift",
    category: "Económico",
  },
];

const MOCK_BADGES = [
  { id: 1, name: "Primer Curso", description: "Completa tu primer curso", earned: true, icon: "star" },
  { id: 2, name: "Racha de 5 días", description: "Ingresa 5 días seguidos", earned: true, icon: "fire" },
  { id: 3, name: "100% Asistencia", description: "Asiste a todas las clases de un curso", earned: false, icon: "check" },
  { id: 4, name: "Explorador", description: "Visualiza todos los recursos de un curso", earned: false, icon: "medal" },
  { id: 5, name: "Top 10", description: "Alcanza el Top 10 del ranking", earned: false, icon: "trophy" },
  { id: 6, name: "Maestro", description: "Completa 5 cursos con nota máxima", earned: false, icon: "school" },
];

const POINT_RULES = [
  { action: "Completar un recurso/actividad", points: "+50 pts" },
  { action: "Completar un curso al 100%", points: "+500 pts" },
  { action: "Asistir a una clase en línea", points: "+100 pts" },
  { action: "Racha diaria (ingreso consecutivo)", points: "+25 pts/día" },
  { action: "Obtener nota máxima en evaluación", points: "+200 pts" },
  { action: "Visualizar todos los recursos de un módulo", points: "+75 pts" },
];

// --- Componentes ---

const Gamification = () => {
  const { showOverlay } = useOrientationLock("portrait");
  const theme = useTheme();
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState("ranking");

  const textColor = readableColor(
    theme.colors.backgroundColorBody,
    "#1a1a1a",
    "#f2f2f2",
    false
  );

  const cardBg = theme.mode === "light" ? "#ffffff" : "#2a2a2a";
  const cardBorder = theme.mode === "light" ? "#e5e7eb" : "#404040";
  const accentColor = theme.mode === "light" ? theme.colors.tertiary_light : theme.colors.tertiary_dark;

  // Puntaje ficticio del usuario actual
  const userPoints = 3100;
  const userRank = 8;

  const tabs = [
    { key: "ranking", label: "Ranking" },
    { key: "rewards", label: "Recompensas" },
    { key: "badges", label: "Insignias" },
  ];

  return (
    <>
      <RotateDeviceOverlay show={showOverlay} />
      <Navbar />
      <div
        className="min-h-screen pt-[100px] pb-16 px-4 tablet:px-8"
        style={{ backgroundColor: theme.colors.backgroundColorBody }}
      >
        <div className="max-w-[120rem] mx-auto">
          {/* Header del usuario */}
          <div
            className="rounded-2xl p-6 tablet:p-10 mb-8 flex flex-col tablet:flex-row items-center gap-6 tablet:gap-10 shadow-lg"
            style={{
              background: `linear-gradient(135deg, ${accentColor}, ${theme.mode === "light" ? theme.colors.primary_light : theme.colors.primary_dark})`,
            }}
          >
            <div className="relative">
              <img
                src={auth.user?.profileimageurlsmall}
                alt="avatar"
                className="size-28 tablet:size-32 rounded-full border-4 border-white/30 shadow-md"
              />
              <div
                className="absolute -bottom-2 -right-2 bg-yellow-400 text-black rounded-full size-12 flex items-center justify-center font-bold text-xl shadow"
              >
                #{userRank}
              </div>
            </div>
            <div className="flex flex-col items-center tablet:items-start gap-2">
              <h1 className="text-4xl tablet:text-5xl font-bold text-white">
                {auth.user?.fullname || "Colaborador Abastible"}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-5 py-2">
                  <StarIcon sx={{ color: "#facc15", fontSize: 28 }} />
                  <span className="text-white text-2xl font-semibold">
                    {userPoints.toLocaleString()} pts
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-5 py-2">
                  <LocalFireDepartmentIcon sx={{ color: "#fb923c", fontSize: 28 }} />
                  <span className="text-white text-2xl font-semibold">4 días de racha</span>
                </div>
              </div>
              <p className="text-white/70 text-xl mt-1">
                Estás a 650 pts de subir al puesto #7. ¡Sigue así!
              </p>
            </div>
          </div>

          {/* Resumen rápido de cómo obtener puntos */}
          <div
            className="rounded-2xl p-6 mb-8 shadow-sm"
            style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
          >
            <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2" style={{ color: textColor }}>
              <StarIcon sx={{ color: "#facc15", fontSize: 24 }} />
              ¿Cómo ganar puntos?
            </h3>
            <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 gap-3">
              {POINT_RULES.map((rule, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ backgroundColor: theme.mode === "light" ? "#f9fafb" : "#333" }}
                >
                  <span className="text-xl" style={{ color: textColor }}>{rule.action}</span>
                  <span
                    className="text-xl font-bold ml-3 whitespace-nowrap"
                    style={{ color: accentColor }}
                  >
                    {rule.points}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-3 mb-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="px-6 py-3 rounded-xl text-2xl font-medium transition-all hover:brightness-110"
                style={{
                  backgroundColor: activeTab === tab.key ? accentColor : cardBg,
                  color: activeTab === tab.key ? readableColor(accentColor) : textColor,
                  border: `1px solid ${activeTab === tab.key ? accentColor : cardBorder}`,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Contenido de tabs */}
          {activeTab === "ranking" && (
            <LeaderboardSection
              data={MOCK_LEADERBOARD}
              textColor={textColor}
              cardBg={cardBg}
              cardBorder={cardBorder}
              accentColor={accentColor}
              theme={theme}
              currentUser={auth.user?.fullname}
            />
          )}
          {activeTab === "rewards" && (
            <RewardsSection
              rewards={MOCK_REWARDS}
              userPoints={userPoints}
              textColor={textColor}
              cardBg={cardBg}
              cardBorder={cardBorder}
              accentColor={accentColor}
              theme={theme}
            />
          )}
          {activeTab === "badges" && (
            <BadgesSection
              badges={MOCK_BADGES}
              textColor={textColor}
              cardBg={cardBg}
              cardBorder={cardBorder}
              accentColor={accentColor}
              theme={theme}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

// --- Leaderboard ---

const LeaderboardSection = ({ data, textColor, cardBg, cardBorder, accentColor, theme, currentUser }) => {
  const podiumColors = ["#facc15", "#c0c0c0", "#cd7f32"];
  const podiumIcons = [
    <EmojiEventsIcon sx={{ fontSize: 48, color: "#facc15" }} />,
    <EmojiEventsIcon sx={{ fontSize: 40, color: "#c0c0c0" }} />,
    <EmojiEventsIcon sx={{ fontSize: 36, color: "#cd7f32" }} />,
  ];

  return (
    <div>
      {/* Podium top 3 */}
      <div className="grid grid-cols-1 tablet:grid-cols-3 gap-6 mb-8">
        {data.slice(0, 3).map((user, idx) => (
          <div
            key={user.rank}
            className="rounded-2xl p-6 flex flex-col items-center gap-3 shadow-md relative overflow-hidden"
            style={{
              backgroundColor: cardBg,
              border: `2px solid ${podiumColors[idx]}40`,
            }}
          >
            <div
              className="absolute top-0 left-0 w-full h-1"
              style={{ backgroundColor: podiumColors[idx] }}
            />
            {podiumIcons[idx]}
            <div
              className="size-20 rounded-full flex items-center justify-center text-3xl font-bold text-white shadow"
              style={{ backgroundColor: podiumColors[idx] + "90" }}
            >
              {user.name.split(" ").map(n => n[0]).join("")}
            </div>
            <p className="text-2xl font-bold" style={{ color: textColor }}>{user.name}</p>
            <div className="flex items-center gap-2">
              <StarIcon sx={{ color: "#facc15", fontSize: 22 }} />
              <span className="text-2xl font-semibold" style={{ color: accentColor }}>
                {user.points.toLocaleString()} pts
              </span>
            </div>
            <div className="flex gap-4 text-lg" style={{ color: textColor + "99" }}>
              <span>{user.courses} cursos</span>
              <span>·</span>
              <span>{user.streak} días racha</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabla resto del ranking */}
      <div
        className="rounded-2xl overflow-hidden shadow-sm"
        style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
      >
        <div
          className="grid grid-cols-[60px_1fr_120px_100px_100px] tablet:grid-cols-[80px_1fr_150px_120px_120px] p-4 text-xl font-semibold"
          style={{ backgroundColor: theme.mode === "light" ? "#f3f4f6" : "#333", color: textColor }}
        >
          <span>#</span>
          <span>Colaborador</span>
          <span className="text-center">Puntos</span>
          <span className="text-center hidden tablet:block">Cursos</span>
          <span className="text-center hidden tablet:block">Racha</span>
        </div>
        {data.slice(3).map((user) => (
          <div
            key={user.rank}
            className="grid grid-cols-[60px_1fr_120px_100px_100px] tablet:grid-cols-[80px_1fr_150px_120px_120px] p-4 items-center text-xl border-t transition-colors hover:brightness-95"
            style={{
              borderColor: cardBorder,
              backgroundColor: user.name === "Roberto Díaz" ? (accentColor + "15") : "transparent",
            }}
          >
            <span className="font-bold text-2xl" style={{ color: textColor }}>{user.rank}</span>
            <div className="flex items-center gap-3">
              <div
                className="size-10 rounded-full flex items-center justify-center text-lg font-semibold text-white"
                style={{ backgroundColor: accentColor + "80" }}
              >
                {user.name.split(" ").map(n => n[0]).join("")}
              </div>
              <span style={{ color: textColor }}>{user.name}</span>
            </div>
            <span className="text-center font-semibold" style={{ color: accentColor }}>
              {user.points.toLocaleString()}
            </span>
            <span className="text-center hidden tablet:block" style={{ color: textColor + "99" }}>
              {user.courses}
            </span>
            <span className="text-center hidden tablet:block" style={{ color: textColor + "99" }}>
              {user.streak} días
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Recompensas ---

const RewardsSection = ({ rewards, userPoints, textColor, cardBg, cardBorder, accentColor, theme }) => {
  const categories = [...new Set(rewards.map(r => r.category))];

  const getRewardIcon = (icon, color) => {
    const sx = { fontSize: 40, color };
    switch (icon) {
      case "school": return <SchoolIcon sx={sx} />;
      case "gift": return <CardGiftcardIcon sx={sx} />;
      case "trophy": return <EmojiEventsIcon sx={sx} />;
      case "medal": return <WorkspacePremiumIcon sx={sx} />;
      default: return <CardGiftcardIcon sx={sx} />;
    }
  };

  return (
    <div>
      {categories.map((cat) => (
        <div key={cat} className="mb-10">
          <h3 className="text-3xl font-bold mb-5" style={{ color: textColor }}>
            {cat}
          </h3>
          <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 bigDesktop:grid-cols-4 gap-6">
            {rewards
              .filter((r) => r.category === cat)
              .map((reward) => {
                const canRedeem = userPoints >= reward.pointsRequired;
                return (
                  <div
                    key={reward.id}
                    className="rounded-2xl p-6 flex flex-col gap-4 shadow-sm transition-all hover:shadow-md"
                    style={{
                      backgroundColor: cardBg,
                      border: `1px solid ${canRedeem ? accentColor + "60" : cardBorder}`,
                      opacity: canRedeem ? 1 : 0.7,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="size-16 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: (canRedeem ? accentColor : "#9ca3af") + "20" }}
                      >
                        {getRewardIcon(reward.icon, canRedeem ? accentColor : "#9ca3af")}
                      </div>
                      <div
                        className="px-4 py-1 rounded-full text-lg font-semibold"
                        style={{
                          backgroundColor: canRedeem ? accentColor + "20" : "#9ca3af20",
                          color: canRedeem ? accentColor : "#9ca3af",
                        }}
                      >
                        {reward.pointsRequired.toLocaleString()} pts
                      </div>
                    </div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: textColor }}>
                        {reward.name}
                      </p>
                      <p className="text-lg mt-1" style={{ color: textColor + "99" }}>
                        {reward.description}
                      </p>
                    </div>
                    <button
                      className="mt-auto w-full py-3 rounded-xl text-xl font-semibold transition-all"
                      style={{
                        backgroundColor: canRedeem ? accentColor : "#d1d5db",
                        color: canRedeem ? readableColor(accentColor) : "#9ca3af",
                        cursor: canRedeem ? "pointer" : "not-allowed",
                      }}
                      disabled={!canRedeem}
                    >
                      {canRedeem ? "Canjear recompensa" : `Faltan ${(reward.pointsRequired - userPoints).toLocaleString()} pts`}
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};

// --- Insignias ---

const BadgesSection = ({ badges, textColor, cardBg, cardBorder, accentColor, theme }) => {
  const getBadgeIcon = (icon, earned) => {
    const color = earned ? accentColor : "#9ca3af";
    const sx = { fontSize: 48, color };
    switch (icon) {
      case "star": return <StarIcon sx={sx} />;
      case "fire": return <LocalFireDepartmentIcon sx={sx} />;
      case "check": return <CheckCircleIcon sx={sx} />;
      case "medal": return <MilitaryTechIcon sx={sx} />;
      case "trophy": return <EmojiEventsIcon sx={sx} />;
      case "school": return <SchoolIcon sx={sx} />;
      default: return <StarIcon sx={sx} />;
    }
  };

  return (
    <div className="grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 bigDesktop:grid-cols-6 gap-6">
      {badges.map((badge) => (
        <div
          key={badge.id}
          className="rounded-2xl p-6 flex flex-col items-center gap-3 text-center shadow-sm transition-all hover:shadow-md"
          style={{
            backgroundColor: cardBg,
            border: `1px solid ${badge.earned ? accentColor + "60" : cardBorder}`,
            opacity: badge.earned ? 1 : 0.5,
          }}
        >
          <div
            className="size-24 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: badge.earned ? accentColor + "20" : "#9ca3af15",
            }}
          >
            {getBadgeIcon(badge.icon, badge.earned)}
          </div>
          <p className="text-2xl font-bold" style={{ color: textColor }}>
            {badge.name}
          </p>
          <p className="text-lg" style={{ color: textColor + "99" }}>
            {badge.description}
          </p>
          {badge.earned && (
            <span
              className="text-lg font-semibold px-3 py-1 rounded-full"
              style={{ backgroundColor: accentColor + "20", color: accentColor }}
            >
              ✓ Obtenida
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default Gamification;
